import {Component} from 'substance'

/**
 * @typedef UISelect.props
 * @property {UISelect.list} list
 */

/**
 * @typedef UISelect.list
 * @property {string} label - Only on top level list
 * @property {string} title - Only on non-top level lists
 * @property {string} type
 * @property {object[]} values
 * @property {string} values.title
 * @property {UISelect.list} values.list - The list to display as a value
 *
 * @example
 * {
 *     label: '[a list label]',
 *     type: '[dropdown/toggle/button]'
 *     values: [
 *         {
 *             title: 'title 1', ...
 *         },
 *         {
 *             title: 'title 2', ...
 *         },
 *         {
 *             title: 'Additional options',
 *             list: {
 *                 title: 'Other options',
 *                 type: '[dropdown/toggle/button]',
 *                 values: [
 *                     ...
 *                 ]
 *             }
 *         }
 *     ]
 * }
 */


/**
 * This component renders a list of selectable items.
 *
 * It supports 3 types of lists: drop-down list, toggle list and button list.
 *
 * The list is defined as a property, that should contain:
 *```js
 * {
 *     label: '[a list label]',
 *     type: '[dropdown/toggle/button]'
 *     values: [
 *         {
 *             title: 'title 1', ...
 *         },
 *         {
 *             title: 'title 2', ...
 *         },
 *         {
 *             title: 'Additional options',
 *             list: {
 *                 title: 'Other options',
 *                 type: '[dropdown/toggle/button]',
 *                 values: [
 *                     ...
 *                 ]
 *             }
 *         }
 *     ]
 * }
 * ```
 *
 * @name UISelect
 * @class
 *
 * @example
 * import {UISelect} from 'writer'
 *
 * render($$) {
 *     const el = $$('div')
 *     return el.append(
 *       $$(UISelect, {
 *           list: this.state.list,
 *           onChangeList: (listItems, selectedItem) => {
 *              this.addToSelectedItems(selectedItem)
 *           },
 *           isSelected: (listItems, item) => {
 *               const selectedItems = this.getSelectedItems()
 *               return selectedItems.some((selectedItem) => return selectedItem.value === item.value)
 *           }
 *       })
 *     )
 * }
 *
 * @param {UISelect.props} props
 * @property {UISelect.props} props
 */
class SelectComponent extends Component {

    /**
     * Renders given list of values as button list group
     *
     * @param $$
     * @param list
     * @returns {*}
     * @private
     */
    renderButtonList($$, list) {
        const values = list.values

        const btnGroup = $$('div').addClass('btn-group btn-group-gmlist btn-group-' + list.name)

        Object.keys(values).forEach(index => {
            const data = values[index]
            const btn = $$('button').addClass('btn btn-secondary')
                .on('click', () => {
                    this.props.onChangeList(list, data)
                })

            if (data.icon) {
                const icon = $$('i')
                    .addClass('fa ' + data.icon)
                    .addClass('im-spaced-right-gmlist')
                btn.append(icon)
            }

            if (data.image) {
                const image = $$('img').attr({
                    src: data.image
                })

                btn.append(image)
            }

            btn.append(data.title)

            if (this.props.isSelected(list, data)) {
                btn.addClass('active')
            }

            btnGroup.append(btn)
        })

        return btnGroup
    }

    /**
     * Renders given list of values as drop down list group
     *
     * @param $$
     * @param list
     * @returns {*}
     * @private
     */
    renderDropDownList($$, list) {
        const values = list.values

        const listGroup = $$('div').addClass('select-group-gmlist select-group-' + list.name)

        let selectList = $$('select').addClass('form-control')
            .on('change', (e) => {
                let selectedIndex = e.currentTarget.selectedIndex
                this.props.onChangeList(list, values[selectedIndex])
            })

        Object.keys(values).forEach(index => {
            const data = values[index]
            let option = $$('option')
                .attr('label', data.title)

            if (this.props.isSelected(list, data)) {
                option.attr('selected', true)
            }

            selectList.append(option)
        })
        listGroup.append(selectList)

        return listGroup
    }

    /**
     * Renders given list of values as toggle group
     *
     * @param $$
     * @param list
     * @returns {*}
     * @private
     */
    renderToggleList($$, list) {
        const values = list.values

        const toggleGroup = $$('div').addClass('toggle-group-gmlist toggle-group-' + list.name)

        Object.keys(values).forEach(index => {
            const data = values[index],
                fakeLabel = $$('span').addClass('toggle-btn-fake-label').append(data.title).on('click', () => {
                    this.props.onChangeList(list, data)
                }),
                label = $$('label').setAttribute('for', data.title).addClass('toggle-btn-label').on('click', e => {
                    e.preventDefault()
                    e.stopPropagation()
                    this.props.onChangeList(list, data)
                }),
                checkBox = $$('input').setAttribute('type', 'checkbox').addClass('toggle-btn').setAttribute('id', data.title)

            if (this.props.isSelected(list, data)) {
                checkBox.setAttribute('checked', 'checked')
            } else {
                checkBox.removeAttribute('checked')
            }

            let icon = ''

            if (data.icon) {
                icon = $$('i')
                    .addClass('fa ' + data.icon)
                    .addClass('im-spaced-right-gmlist')
            }

            if (data.image) {
                icon = $$('img').attr({
                    src: data.image
                })
            }

            toggleGroup.append($$('div').addClass('toggle-btn-wrapper').append([checkBox, label, icon, fakeLabel]))
        })

        return toggleGroup
    }

    /**
     * Renders given list of values (handles recursive rendering for nested lists, if available)
     *
     * @param $$
     * @param el
     * @param list
     * @returns {*}
     * @private
     */
    renderList($$, el, list) {
        /* Insert a label and the list to el */
        el.append($$('h2').append(list.label))

        if (list.type === 'button') {
            el.append(this.renderButtonList($$, list))
        }
        else if (list.type === 'dropdown') {
            el.append(this.renderDropDownList($$, list))
        }
        else if (list.type === 'toggle') {
            el.append(this.renderToggleList($$, list))
        }

        for(let i = 0; i < list.values.length; i++) {
            const data = list.values[i]
            if (this.props.isSelected(list, data) && data.hasOwnProperty('list')) {
                this.renderList($$, el, data.list)
            }
        }

        return el
    }

    render($$) {
        const el = $$('div')
        this.renderList($$, el, this.props.list)

        return el
    }
}

export {SelectComponent}
